import type { LibraryFolderEntry } from './folderPath';

/**
 * The orders a library's folders can be listed in — the row under the header and the "All
 * folders" tree share one. Folders carry no dates of their own (the API stores a name, a parent
 * and a colour), so "Last modified" is the folder's contents: the latest change to any media in it
 * or under it. A folder with none has no such date and sorts last. Ties go to the name, so every
 * order is stable.
 */
export const FOLDER_SORT_OPTIONS = [
  [
    { value: 'name-asc', label: 'Name (A–Z)' },
    { value: 'name-desc', label: 'Name (Z–A)' },
  ],
  [{ value: 'modified-desc', label: 'Last modified' }],
  [
    { value: 'media-desc', label: 'Most media' },
    { value: 'media-asc', label: 'Least media' },
  ],
] as const;

export type FolderSortKey = (typeof FOLDER_SORT_OPTIONS)[number][number]['value'];

export const DEFAULT_FOLDER_SORT: FolderSortKey = 'name-asc';

export const isFolderSortKey = (value: unknown): value is FolderSortKey =>
  FOLDER_SORT_OPTIONS.some((group) => group.some((option) => option.value === value));

export const folderSortLabel = (key: FolderSortKey): string =>
  FOLDER_SORT_OPTIONS.flat().find((option) => option.value === key)?.label ?? '';

/** What a sort needs to know about a folder beyond its entry, counted across its whole subtree. */
export type FolderSortFacts = {
  mediaCount: number;
  /** Latest `updatedAt` of the media under it, in ms; absent when it holds none. */
  lastModifiedAt?: number;
};

export type FolderComparator = (a: LibraryFolderEntry, b: LibraryFolderEntry) => number;

const byName: FolderComparator = (a, b) =>
  a.name.localeCompare(b.name, undefined, { numeric: true });

export const folderComparator = (
  key: FolderSortKey,
  factsOf: (entry: LibraryFolderEntry) => FolderSortFacts,
): FolderComparator => {
  switch (key) {
    case 'name-asc':
      return byName;
    case 'name-desc':
      return (a, b) => byName(b, a);
    case 'modified-desc':
      return (a, b) =>
        (factsOf(b).lastModifiedAt ?? -Infinity) - (factsOf(a).lastModifiedAt ?? -Infinity) ||
        byName(a, b);
    case 'media-desc':
      return (a, b) => factsOf(b).mediaCount - factsOf(a).mediaCount || byName(a, b);
    case 'media-asc':
      return (a, b) => factsOf(a).mediaCount - factsOf(b).mediaCount || byName(a, b);
  }
};
